import {
    createSlice,
    createEntityAdapter,
    EntityState,
    PayloadAction,
    createAsyncThunk
} from '@reduxjs/toolkit';
import type { RootState, AppDispatch } from '../index';
import { 
    collection, onSnapshot, query, where, doc, 
    setDoc, deleteDoc, updateDoc, Timestamp, addDoc
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
// Adjust import path as needed
import type { 
    VisitedLocation as FirestoreVisitedLocation, 
    Review as FirestoreReview, 
    FirestoreLocation,
} from '@/types/firestore'; 

// Serializable version for Redux state
interface ReduxVisitedLocation extends Omit<FirestoreVisitedLocation, 'visitDate' | 'id' | 'locationId'> {
    id: string; // Use locationId as the primary key for the entity adapter
    locationId: string; // Keep original locationId if needed elsewhere
    visitDate: number; // Store timestamp as number (milliseconds)
}

// Serializable Review for Redux state
export interface ReduxReview extends Omit<FirestoreReview, 'date' | 'id'> {
    id: string; // Ensure id is required and string
    date: number; // Store timestamp as number (milliseconds)
}

// Type for loading status
type LoadingStatus = 'idle' | 'pending' | 'succeeded' | 'failed';

// Adapters using Redux types (Rely on default 'id' field)
// The `id` field in ReduxVisitedLocation/ReduxReview MUST be the key.
const visitedLocationsAdapter = createEntityAdapter<ReduxVisitedLocation>({
    // selectId is not needed if the entity has a top-level `id` field
});

const reviewsAdapter = createEntityAdapter<ReduxReview>({
    // selectId is not needed if the entity has a top-level `id` field
});

// State Shape using Redux types and specifying string ID
interface UserSpecificState {
    userId: string | null;
    visitedLocations: EntityState<ReduxVisitedLocation, string>; // Use Redux type
    reviews: EntityState<ReduxReview, string>; // Use Redux type
    loadingVisited: LoadingStatus;
    loadingReviews: LoadingStatus;
    operationLoading: {
        toggleVisited: LoadingStatus;
        updateRating: LoadingStatus;
        updateVisitedLocation: LoadingStatus; // Add new loading state for updateVisitedLocation
        addReview: LoadingStatus;
        updateReview: LoadingStatus;
        deleteReview: LoadingStatus;
        updatePublicNote: LoadingStatus;
    };
    error: string | null; // Combined error state for simplicity
    listenersActive: boolean; // Track if listeners are set up for the current user
    visitedLocationsListenerUnsubscribe: (() => void) | null;
    reviewsListenerUnsubscribe: (() => void) | null;
}

// Initial State 
const initialState: UserSpecificState = {
    userId: null,
    visitedLocations: visitedLocationsAdapter.getInitialState(), // Use adapter's initial state
    reviews: reviewsAdapter.getInitialState(), // Use adapter's initial state
    loadingVisited: 'idle',
    loadingReviews: 'idle',
    operationLoading: {
        toggleVisited: 'idle',
        updateRating: 'idle',
        updateVisitedLocation: 'idle',
        addReview: 'idle',
        updateReview: 'idle',
        deleteReview: 'idle',
        updatePublicNote: 'idle',
    },
    error: null,
    listenersActive: false,
    visitedLocationsListenerUnsubscribe: null,
    reviewsListenerUnsubscribe: null,
};

// Keep track of listener unsubscribes
let visitedListenerUnsubscribe: (() => void) | null = null;
let reviewsListenerUnsubscribe: (() => void) | null = null;

// --- Thunks ---

// Thunk to initialize listeners for user-specific data
export const initializeUserSpecificData = createAsyncThunk<
    void,
    string, // Argument is userId
    { dispatch: AppDispatch; rejectValue: string; state: RootState }
>(
    'userSpecific/initializeData',
    async (userId, { dispatch, rejectWithValue, getState }) => {
        const { userId: currentUserId, listenersActive } = getState().userSpecific;
        
        // Clear previous listeners if user changes or listeners are already active for a different user
        if (listenersActive && currentUserId !== userId) {
            if (visitedListenerUnsubscribe) visitedListenerUnsubscribe();
            if (reviewsListenerUnsubscribe) reviewsListenerUnsubscribe();
            visitedListenerUnsubscribe = null;
            reviewsListenerUnsubscribe = null;
            dispatch(userSpecificSlice.actions.resetStateAndListeners()); // Reset state fully
        } else if (listenersActive && currentUserId === userId) {
            return; // Already listening for this user
        }

        if (!userId) {
             if (listenersActive) { // Cleanup if were active
                if (visitedListenerUnsubscribe) visitedListenerUnsubscribe();
                if (reviewsListenerUnsubscribe) reviewsListenerUnsubscribe();
                visitedListenerUnsubscribe = null;
                reviewsListenerUnsubscribe = null;
             }
             dispatch(userSpecificSlice.actions.resetStateAndListeners()); 
             return rejectWithValue('No user ID provided for initialization.');
        }
        
        dispatch(userSpecificSlice.actions.setUserId(userId));
        dispatch(userSpecificSlice.actions.setLoading({ loadingVisited: 'pending', loadingReviews: 'pending' }));
        dispatch(userSpecificSlice.actions.setError(null)); // Clear previous errors

        let visitedSuccess = false;
        let reviewsSuccess = false;

        // Listener for Visited Locations
        try {
            const visitedQuery = query(collection(db, `users/${userId}/visitedLocations`));
            visitedListenerUnsubscribe = onSnapshot(visitedQuery, 
                (snapshot) => {
                    try {
                        const visitedData = snapshot.docs
                            .map(doc => {
                                try {
                                    const data = doc.data();
                                    const locationId = doc.id; // This is the key
                                    if (!data || !locationId) return null; 

                                    // Create ReduxVisitedLocation with id = locationId
                                    const reduxVisited: ReduxVisitedLocation = {
                                        id: locationId, // Set required id field for adapter
                                        locationId: locationId, // Keep original locationId field
                                        ...(data as Omit<FirestoreVisitedLocation, 'visitDate' | 'id' | 'locationId'>), 
                                        visitDate: data.visitDate instanceof Timestamp 
                                            ? data.visitDate.toMillis() 
                                            : 0, 
                                    };
                                    return reduxVisited;
                                } catch (e) {
                                    console.warn(`[UserSpecific] Error processing visited location doc ${doc.id}:`, e);
                                    return null;
                                }
                            })
                            .filter((item): item is ReduxVisitedLocation => item !== null); 

                        // Dispatch payload with Firestore Timestamps
                        dispatch(userSpecificSlice.actions.visitedLocationsReceived(visitedData));
                        dispatch(userSpecificSlice.actions.setLoading({ loadingVisited: 'succeeded' }));
                        visitedSuccess = true;
                        if (visitedSuccess && reviewsSuccess) {
                            dispatch(userSpecificSlice.actions.setListenersActive(true));
                        }
                    } catch (error: any) {
                        console.error('[UserSpecific] Error updating visited locations store:', error);
                        dispatch(userSpecificSlice.actions.setError(`Failed to update locations store: ${error.message}`));
                    }
                }, 
                (error) => {
                    console.error(`[UserSpecific] Error listening to visited locations for ${userId}:`, error);
                    dispatch(userSpecificSlice.actions.setError(error.message));
                    dispatch(userSpecificSlice.actions.setLoading({ loadingVisited: 'failed' }));
                }
            );
        } catch (error: any) {
            console.error(`[UserSpecific] Failed to set up visited locations listener for ${userId}:`, error);
            dispatch(userSpecificSlice.actions.setError(error.message));
            dispatch(userSpecificSlice.actions.setLoading({ loadingVisited: 'failed' }));
            visitedListenerUnsubscribe = null;
        }

        // Listener for Reviews
        try {
            const reviewsQuery = query(collection(db, 'reviews'), where('userId', '==', userId));
            reviewsListenerUnsubscribe = onSnapshot(reviewsQuery, 
                (snapshot) => {
                    try {
                        const reviewsData = snapshot.docs
                            .map(doc => {
                                try {
                                    const data = doc.data();
                                    const reviewId = doc.id; // This is the key
                                    if (!data || !reviewId) return null; 

                                    // Create ReduxReview with required id
                                    const reduxReview: ReduxReview = {
                                        id: reviewId, 
                                        ...(data as Omit<FirestoreReview, 'date' | 'id'>), 
                                        date: data.date instanceof Timestamp 
                                            ? data.date.toMillis() 
                                            : 0,
                                    };
                                    return reduxReview;
                                } catch (e) {
                                    console.warn(`[UserSpecific] Error processing review doc ${doc.id}:`, e);
                                    return null;
                                }
                            })
                            .filter((item): item is ReduxReview => item !== null); 
                            
                        // Dispatch payload with Firestore Timestamps
                        dispatch(userSpecificSlice.actions.reviewsReceived(reviewsData));
                        dispatch(userSpecificSlice.actions.setLoading({ loadingReviews: 'succeeded' }));
                        reviewsSuccess = true;
                        if (visitedSuccess && reviewsSuccess) {
                            dispatch(userSpecificSlice.actions.setListenersActive(true));
                        }
                    } catch (error: any) {
                        console.error('[UserSpecific] Error updating reviews store:', error);
                        dispatch(userSpecificSlice.actions.setError(`Failed to update reviews store: ${error.message}`));
                    }
                }, 
                (error) => {
                    console.error(`[UserSpecific] Error listening to reviews for ${userId}:`, error);
                    // Don't overwrite visited error if it occurred
                    if (!getState().userSpecific.error) {
                         dispatch(userSpecificSlice.actions.setError(error.message));
                    }
                    dispatch(userSpecificSlice.actions.setLoading({ loadingReviews: 'failed' }));
                }
            );
        } catch (error: any) {
             console.error(`[UserSpecific] Failed to set up reviews listener for ${userId}:`, error);
             if (!getState().userSpecific.error) {
                 dispatch(userSpecificSlice.actions.setError(error.message));
             }
            dispatch(userSpecificSlice.actions.setLoading({ loadingReviews: 'failed' }));
            reviewsListenerUnsubscribe = null;
        }
        
        // Note: Thunk resolves immediately, listeners update state asynchronously.
    }
);

// Thunk to cleanup listeners (e.g., on logout)
export const cleanupUserSpecificListeners = createAsyncThunk<void, void, { dispatch: AppDispatch } >(
    'userSpecific/cleanupListeners',
    (_, { dispatch }) => {
        if (visitedListenerUnsubscribe) visitedListenerUnsubscribe();
        if (reviewsListenerUnsubscribe) reviewsListenerUnsubscribe();
        visitedListenerUnsubscribe = null;
        reviewsListenerUnsubscribe = null;
        dispatch(userSpecificSlice.actions.resetStateAndListeners());
    }
);

// --- CRUD Thunks --- 

// Thunk to toggle visited status
export const toggleVisited = createAsyncThunk<
    void,
    { location: FirestoreLocation; currentVisitedState: boolean }, // Expect FirestoreLocation from component
    { dispatch: AppDispatch; rejectValue: string; state: RootState }
>(
    'userSpecific/toggleVisited',
    async ({ location, currentVisitedState }, { dispatch, getState, rejectWithValue }) => {
        const { userId } = getState().userSpecific;
        if (!userId) return rejectWithValue('User not logged in');
        if (!location || !location.id) return rejectWithValue('Invalid location data');

        const locationId = location.id;
        const visitedDocRef = doc(db, `users/${userId}/visitedLocations`, locationId);
        
        dispatch(userSpecificSlice.actions.setOperationLoading({ operation: 'toggleVisited', status: 'pending' }));

        try {
            if (currentVisitedState) {
                // Remove visited location
                await deleteDoc(visitedDocRef);
                // State updates via listener
            } else {
                // Add visited location
                // Prepare data with Firestore Timestamp
                const visitedData: FirestoreVisitedLocation = {
                    locationId: locationId,
                    locationName: location.name,
                    rating: 0, // Default rating
                    publicNote: '', // Default empty notes
                    privateNote: '',
                    visitDate: Timestamp.now() // Use Firestore Timestamp for writing
                };
                await setDoc(visitedDocRef, visitedData);
                // State updates via listener
            }
            dispatch(userSpecificSlice.actions.setOperationLoading({ operation: 'toggleVisited', status: 'succeeded' }));
        } catch (error: any) {
            console.error(`[UserSpecific] Error toggling visited status for ${locationId}:`, error);
            dispatch(userSpecificSlice.actions.setOperationLoading({ operation: 'toggleVisited', status: 'failed' }));
            return rejectWithValue(error.message);
        }
    }
);

// Thunk to update rating
export const updateRating = createAsyncThunk<
    void,
    { locationId: string; rating: number },
    { dispatch: AppDispatch; rejectValue: string; state: RootState }
>(
    'userSpecific/updateRating',
    async ({ locationId, rating }, { dispatch, getState, rejectWithValue }) => {
        const { userId } = getState().userSpecific;
        if (!userId) return rejectWithValue('User not logged in');
        if (!locationId) return rejectWithValue('Invalid location ID');
        if (rating < 0 || rating > 5) return rejectWithValue('Invalid rating value');

        const visitedDocRef = doc(db, `users/${userId}/visitedLocations`, locationId);
        dispatch(userSpecificSlice.actions.setOperationLoading({ operation: 'updateRating', status: 'pending' }));

        try {
            // Check if the location is already marked as visited in the state
            const existingVisited = visitedLocationsSelectors.selectById(getState(), locationId);

            if (existingVisited) {
                // Update existing visited location rating
                await updateDoc(visitedDocRef, { rating: rating });
            } else {
                // If not visited, we need the location name to add it
                // This requires fetching the location details first or ensuring name is passed
                // For simplicity, assume it must be visited first to rate, or adjust toggleVisited logic
                console.warn(`[UserSpecific] Cannot rate location ${locationId} as it's not marked visited.`);
                // Alternative: Fetch location details here if necessary
                // const locationDoc = await getDoc(doc(db, 'locations', locationId));
                // if (!locationDoc.exists()) throw new Error('Location not found');
                // const locationName = locationDoc.data().name;
                // await setDoc(visitedDocRef, { locationId, locationName, rating, visitDate: Timestamp.now(), ... });
                dispatch(userSpecificSlice.actions.setOperationLoading({ operation: 'updateRating', status: 'failed' }));
                return rejectWithValue('Location must be visited first to rate.');
            }
            // State updates via listener
            dispatch(userSpecificSlice.actions.setOperationLoading({ operation: 'updateRating', status: 'succeeded' }));
        } catch (error: any) {
            console.error(`[UserSpecific] Error updating rating for ${locationId}:`, error);
            dispatch(userSpecificSlice.actions.setOperationLoading({ operation: 'updateRating', status: 'failed' }));
            return rejectWithValue(error.message);
        }
    }
);

// Thunk to update visited location notes
export const updateVisitedLocation = createAsyncThunk<
    void,
    { locationId: string; data: { publicNote: string | null; privateNote: string | null } },
    { dispatch: AppDispatch; rejectValue: string; state: RootState }
>(
    'userSpecific/updateVisitedLocation',
    async ({ locationId, data }, { dispatch, getState, rejectWithValue }) => {
        const { userId } = getState().userSpecific;
        if (!userId) return rejectWithValue('User not logged in');
        if (!locationId) return rejectWithValue('Invalid location ID');

        const visitedDocRef = doc(db, `users/${userId}/visitedLocations`, locationId);
        dispatch(userSpecificSlice.actions.setOperationLoading({ 
            operation: 'updateVisitedLocation',
            status: 'pending' 
        }));

        try {
            // Check if the location is already marked as visited in the state
            const existingVisited = visitedLocationsSelectors.selectById(getState(), locationId);

            if (existingVisited) {
                // Update existing visited location notes
                await updateDoc(visitedDocRef, { 
                    publicNote: data.publicNote, 
                    privateNote: data.privateNote 
                });
            } else {
                dispatch(userSpecificSlice.actions.setOperationLoading({ 
                    operation: 'updateVisitedLocation',
                    status: 'failed' 
                }));
                return rejectWithValue('Location must be visited first to add notes.');
            }
            // State updates via listener
            dispatch(userSpecificSlice.actions.setOperationLoading({ 
                operation: 'updateVisitedLocation',
                status: 'succeeded' 
            }));
        } catch (error: any) {
            console.error(`[UserSpecific] Error updating notes for ${locationId}:`, error);
            dispatch(userSpecificSlice.actions.setOperationLoading({ 
                operation: 'updateVisitedLocation',
                status: 'failed' 
            }));
            return rejectWithValue(error.message);
        }
    }
);

// Add a new thunk to update a public note
export const updatePublicNote = createAsyncThunk<
  void,
  { authorUserId: string; locationId: string; note: string | null },
  { rejectValue: string }
>(
  'userSpecific/updatePublicNote',
  async ({ authorUserId, locationId, note }, { rejectWithValue }) => {
    try {
      console.log(`[UserSpecificSlice] Updating public note for user ${authorUserId} at location ${locationId}`);
      const noteDocRef = doc(db, `users/${authorUserId}/visitedLocations`, locationId);
      
      // Update just the publicNote field
      await updateDoc(noteDocRef, { publicNote: note });
      
      return;
    } catch (error: any) {
      console.error(`Error updating public note for user ${authorUserId} at location ${locationId}:`, error);
      return rejectWithValue(error.message || 'Failed to update public note');
    }
  }
);

// --- Reviews Thunks --- 
// Ensure these use FirestoreReview where appropriate
export const addReview = createAsyncThunk<
    ReduxReview, // Return Redux type
    Omit<FirestoreReview, 'id' | 'date'>, 
    { rejectValue: string; state: RootState }
>(
    'userSpecific/addReview',
    async (reviewData, { getState, rejectWithValue }) => {
        const { userId } = getState().userSpecific;
        if (!userId || userId !== reviewData.userId) return rejectWithValue('Auth mismatch or missing user ID');
        
        try {
            const dataWithTimestamp = {
                ...reviewData,
                date: Timestamp.now(), // Use Firestore Timestamp for write
            };
            const docRef = await addDoc(collection(db, 'reviews'), dataWithTimestamp);
            // Return ReduxReview for the reducer
            const newReview: ReduxReview = {
                id: docRef.id,
                ...(reviewData as Omit<FirestoreReview, 'id' | 'date'>),
                date: dataWithTimestamp.date.toMillis(), // Convert to number for Redux state
            };
            return newReview;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to add review');
        }
    }
);

export const updateReview = createAsyncThunk<
    { id: string; changes: Partial<ReduxReview> }, // Return changes as Redux type
    { id: string; data: Partial<Omit<FirestoreReview, 'id' | 'date' | 'userId'>> }, 
    { rejectValue: string; state: RootState }
>(
    'userSpecific/updateReview',
    async ({ id, data }, { getState, rejectWithValue }) => {
        const { userId } = getState().userSpecific;
        if (!userId) return rejectWithValue('Auth mismatch or missing user ID');
        
        try {
            const docRef = doc(db, 'reviews', id);
            const dataWithTimestamp = { ...data, date: Timestamp.now() }; // Use Firestore Timestamp for write
            await updateDoc(docRef, dataWithTimestamp);
            // Return changes as ReduxReview partial for reducer
            const changesForReducer: Partial<ReduxReview> = {
                ...data, // Spread original data passed
                date: dataWithTimestamp.date.toMillis() // Convert timestamp to number
            };
            return { id, changes: changesForReducer }; 
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to update review');
        }
    }
);

export const deleteReview = createAsyncThunk<
    string, // Return review ID
    string, // Argument: review ID
    { rejectValue: string; state: RootState }
>(
    'userSpecific/deleteReview',
    async (reviewId, { getState, rejectWithValue }) => {
         const { userId } = getState().userSpecific;
         // Add check: Fetch review first to ensure user owns it? Or rely on Firestore rules.
         if (!userId) return rejectWithValue('Auth mismatch or missing user ID');
         
        try {
            const docRef = doc(db, 'reviews', reviewId);
            // Check ownership before delete (optional)
            await deleteDoc(docRef);
            return reviewId;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to delete review');
        }
    }
);


// --- Slice Definition ---
const userSpecificSlice = createSlice({
    name: 'userSpecific',
    initialState,
    reducers: {
        setUserId: (state, action: PayloadAction<string | null>) => {
            state.userId = action.payload;
        },
        setLoading: (state, action: PayloadAction<{ loadingVisited?: LoadingStatus; loadingReviews?: LoadingStatus }>) => {
            if (action.payload.loadingVisited !== undefined) {
                state.loadingVisited = action.payload.loadingVisited;
            }
            if (action.payload.loadingReviews !== undefined) {
                state.loadingReviews = action.payload.loadingReviews;
            }
        },
        setOperationLoading: (state, action: PayloadAction<{ operation: keyof UserSpecificState['operationLoading']; status: LoadingStatus }>) => {
            state.operationLoading[action.payload.operation] = action.payload.status;
        },
        setError: (state, action: PayloadAction<string | null>) => {
            state.error = action.payload;
        },
        setListenersActive: (state, action: PayloadAction<boolean>) => {
            state.listenersActive = action.payload;
        },
        resetStateAndListeners: (state) => {
            // Clear data using adapters
            visitedLocationsAdapter.removeAll(state.visitedLocations);
            reviewsAdapter.removeAll(state.reviews);
            // Reset other fields
            state.userId = null;
            state.loadingVisited = 'idle';
            state.loadingReviews = 'idle';
            state.operationLoading = initialState.operationLoading; // Reset all operation loadings
            state.error = null;
            state.listenersActive = false;
        },
        // Reducer using Firestore type
        visitedLocationsReceived: (state, action: PayloadAction<ReduxVisitedLocation[]>) => {
            visitedLocationsAdapter.setAll(state.visitedLocations, action.payload); // Expects ReduxVisitedLocation[]
            state.loadingVisited = 'succeeded'; 
        },
        // Reducer using Firestore type
        reviewsReceived: (state, action: PayloadAction<ReduxReview[]>) => {
            reviewsAdapter.setAll(state.reviews, action.payload); // Expects ReduxReview[]
            state.loadingReviews = 'succeeded'; 
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(initializeUserSpecificData.rejected, (state, action) => {
                state.error = action.payload ?? 'Failed to initialize user data listeners';
                state.loadingVisited = 'failed';
                state.loadingReviews = 'failed';
                state.listenersActive = false;
            })
            // Toggle Visited
            .addCase(toggleVisited.pending, (state) => {
                state.operationLoading.toggleVisited = 'pending';
                state.error = null;
            })
            .addCase(toggleVisited.fulfilled, (state) => {
                state.operationLoading.toggleVisited = 'succeeded';
                // Optimistic update handled by listener, but ensure loading state is correct
            })
            .addCase(toggleVisited.rejected, (state, action) => {
                state.operationLoading.toggleVisited = 'failed';
                state.error = action.payload ?? 'Failed to toggle visited status';
            })
            // Update Rating
            .addCase(updateRating.pending, (state) => {
                state.operationLoading.updateRating = 'pending';
                state.error = null;
            })
            .addCase(updateRating.fulfilled, (state) => {
                state.operationLoading.updateRating = 'succeeded';
                 // Optimistic update handled by listener
                 // Could manually update here too if listener lag is an issue
                 // visitedLocationsAdapter.updateOne(state.visitedLocations, { id: action.payload.locationId, changes: { rating: action.payload.rating } });
            })
            .addCase(updateRating.rejected, (state, action) => {
                state.operationLoading.updateRating = 'failed';
                state.error = action.payload ?? 'Failed to update rating';
            })
            // Update Visited Location
            .addCase(updateVisitedLocation.pending, (state) => {
                state.operationLoading.updateVisitedLocation = 'pending';
                state.error = null;
            })
            .addCase(updateVisitedLocation.fulfilled, (state) => {
                state.operationLoading.updateVisitedLocation = 'succeeded';
                // State update handled by listener
            })
            .addCase(updateVisitedLocation.rejected, (state, action) => {
                state.operationLoading.updateVisitedLocation = 'failed';
                state.error = action.payload ?? 'Failed to update location notes';
            })
             // Add Review
            .addCase(addReview.pending, (state) => {
                state.operationLoading.addReview = 'pending';
                state.error = null;
            })
            .addCase(addReview.fulfilled, (state, action) => {
                state.operationLoading.addReview = 'succeeded';
                reviewsAdapter.addOne(state.reviews, action.payload); // Payload is ReduxReview
            })
            .addCase(addReview.rejected, (state, action) => {
                state.operationLoading.addReview = 'failed';
                state.error = action.payload ?? 'Failed to add review';
            })
            // Update Review
            .addCase(updateReview.pending, (state) => {
                state.operationLoading.updateReview = 'pending';
                state.error = null;
            })
            .addCase(updateReview.fulfilled, (state, action) => {
                state.operationLoading.updateReview = 'succeeded';
                // Payload is { id, changes: Partial<ReduxReview> }, compatible with adapter
                reviewsAdapter.updateOne(state.reviews, { 
                    id: action.payload.id, 
                    changes: action.payload.changes 
                });
            })
            .addCase(updateReview.rejected, (state, action) => {
                state.operationLoading.updateReview = 'failed';
                state.error = action.payload ?? 'Failed to update review';
            })
             // Delete Review
            .addCase(deleteReview.pending, (state) => {
                state.operationLoading.deleteReview = 'pending';
                state.error = null;
            })
            .addCase(deleteReview.fulfilled, (state, action) => {
                state.operationLoading.deleteReview = 'succeeded';
                reviewsAdapter.removeOne(state.reviews, action.payload); 
            })
            .addCase(deleteReview.rejected, (state, action) => {
                state.operationLoading.deleteReview = 'failed';
                state.error = action.payload ?? 'Failed to delete review';
            })
            // updatePublicNote cases
            .addCase(updatePublicNote.pending, (state) => {
                if (!state.operationLoading.updatePublicNote) {
                    state.operationLoading.updatePublicNote = 'pending';
                }
            })
            .addCase(updatePublicNote.fulfilled, (state) => {
                state.operationLoading.updatePublicNote = 'succeeded';
                // The actual data update will be handled by the Firestore listener
            })
            .addCase(updatePublicNote.rejected, (state, action) => {
                state.operationLoading.updatePublicNote = 'failed';
                state.error = action.payload || 'Failed to update public note';
            });
    },
});

// Export actions and reducer
export const {
    setUserId,
    setLoading,
    setOperationLoading,
    setError,
    setListenersActive,
    resetStateAndListeners,
    visitedLocationsReceived,
    reviewsReceived
} = userSpecificSlice.actions;
export default userSpecificSlice.reducer;

// --- Selectors ---
export const selectUserSpecificUserId = (state: RootState) => state.userSpecific.userId;
export const selectUserSpecificError = (state: RootState) => state.userSpecific.error;
export const selectUserSpecificListenersActive = (state: RootState) => state.userSpecific.listenersActive;
export const selectVisitedLoading = (state: RootState) => state.userSpecific.loadingVisited;
export const selectReviewsLoading = (state: RootState) => state.userSpecific.loadingReviews;
export const selectUserSpecificOperationLoading = (state: RootState) => state.userSpecific.operationLoading;

// Get adapter selectors
export const visitedLocationsSelectors = visitedLocationsAdapter.getSelectors<RootState>(
    (state) => state.userSpecific.visitedLocations
);

export const reviewsSelectors = reviewsAdapter.getSelectors<RootState>(
    (state) => state.userSpecific.reviews
);

// Selectors return Redux* types
export const selectAllVisitedLocations = visitedLocationsSelectors.selectAll; // Returns ReduxVisitedLocation[]
export const selectVisitedLocationById = visitedLocationsSelectors.selectById; // Returns ReduxVisitedLocation | undefined

// Selector to check if a location is visited by ID
export const selectIsLocationVisited = (locationId: string | null | undefined) => (state: RootState): boolean => {
    if (!locationId) return false;
    return visitedLocationsSelectors.selectIds(state).includes(locationId);
};

// Selector to get rating for a visited location by ID
export const selectVisitedLocationRating = (locationId: string | null | undefined) => (state: RootState): number | null => {
    if (!locationId) return null;
    const visited = visitedLocationsSelectors.selectById(state, locationId); // Returns ReduxVisitedLocation
    return visited ? visited.rating : null;
};

// Selectors for reviews
export const selectAllReviews = reviewsSelectors.selectAll; // Returns ReduxReview[]
export const selectReviewById = reviewsSelectors.selectById; // Returns ReduxReview | undefined